import React from 'react';
import TabContext from '@mui/lab/TabContext';
import assistantClient from '@requests/telnyx/assistants.requests';

import { useSelector } from 'react-redux';
import { TabList, TabPanel } from '@mui/lab';
import { Alert, Divider, Stack, Tab } from '@mui/material';
import { ArticleOutlined, SecurityOutlined } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { NodeAuthProvider, selectCommandByNodeId, State } from '@store/index';
import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { NodeData } from '@common/Node/NodeSidebar/NodeData';
import { AssistantSelector } from '@common/Node/NodeSidebar/Custom/AssistantSelector';
import { AIAssistant } from '@requests/telnyx';
import { RequestError } from '@requests/common';
import { AssistantRouterNodeState } from '../../types';
import { useSecrets } from '@common/hooks/useSecrets';
import { isPaidModel } from '@common/Autocomplete/Base';
import { getModelKey, getModelName, ModelAuthKey } from '@common/NodeAuth/ModelAuthKey';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@common/Button';
import { SubText } from '@common/SubText/SubText';
import { AssistantRouterSystem } from '@common/Node/NodeSidebar/Custom/AssistantRouterSystem';

interface Props {
  tab: string;
  setTab: React.Dispatch<string>;
}

export const AssistantRouterTabs: React.FC<Props> = ({ tab, setTab }) => {
  const navigate = useNavigate();

  const { service_id } = useParams();
  const { secrets, loading, refetch, error } = useSecrets();

  const themeType = useSelector((state: State) => state.flow.theme);
  const authentication = useSelector((state: State) => state.tabs.active.flow?.authentication);

  const { data: assistants = [], isLoading } = useQuery<AIAssistant[], RequestError>({
    queryKey: ['ai_assistants'],
    queryFn: assistantClient.list,
    retry: false,
  });

  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  if (!command) return;

  const state = command.state as AssistantRouterNodeState;

  const assistantWithPaidModel = state.assistants.some((assistantId) => {
    if (isLoading) return false;
    const assistant = assistants.find((assistant) => assistant.id === assistantId);
    if (assistant && isPaidModel(assistant.model)) return true;
    return false;
  });

  const modelAuthKey = getModelKey(state.model);
  const modelAuthRequired = isPaidModel(state.model) && modelAuthKey && !authentication?.[modelAuthKey];
  const showAuthTab = Boolean(modelAuthRequired);

  const fields = command.fields;
  const background = themeType === 'dark' ? '#7272fa' : command.config.background;

  const nameField = fields.find((field) => field.id === 'name');
  const routerMethodField = fields.find((field) => field.id === 'router_method');
  const modelField = fields.find((field) => field.id === 'model');
  const queryField = fields.find((field) => field.id === 'query');
  const systemField = fields.find((field) => field.id === 'system');
  const sessionIdField = fields.find((field) => field.id === 'session_id');
  const includeSessionToolsField = fields.find((field) => field.id === 'include_session_tools');
  const senderIdField = fields.find((field) => field.id === 'sender_id');
  const assistantsField = fields.find((field) => field.id === 'assistants');
  const rewriteQuestionField = fields.find((field) => field.id === 'rewrite_question');
  const filesField = fields.find((field) => field.id === 'files');

  const openAssistants = () => {
    navigate(`/${service_id}/assistants`);
  };

  const assistantModelAuth = new Map<keyof NodeAuthProvider, string>();

  // get a list of all assistants that use models that require auth
  for (const assistantId of state.assistants) {
    const assistant = assistants.find((assistant) => assistant.id === assistantId);
    const modelKey = getModelKey(assistant?.model || '');
    const modelName = getModelName(assistant?.model || '');

    if (modelKey !== null && !assistantModelAuth.has(modelKey) && modelKey && modelName !== 'Authenticated') {
      assistantModelAuth.set(modelKey, modelName);
    }
  }

  return (
    <NodeSidebar>
      <TabContext value={showAuthTab ? '4' : tab}>
        <NodeSidebarTabsContainer color={background}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disabled={showAuthTab} disableRipple label='Node' value='1' />
            <Tab disabled={showAuthTab} disableRipple label='Router' value='2' />
            <Tab
              disabled={showAuthTab}
              title='Data'
              disableRipple
              iconPosition='start'
              icon={<ArticleOutlined />}
              value='3'
            />
            <Tab
              title='Auth'
              disabled={!(assistantWithPaidModel || isPaidModel(state.model))}
              disableRipple
              iconPosition='start'
              icon={<SecurityOutlined />}
              value='4'
            />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {nameField && <NodeInputExpression field={nameField} command={command} />}
            {assistantsField && <AssistantSelector field={assistantsField} command={command} />}
            {queryField && <NodeInputExpression field={queryField} command={command} />}
            {filesField && <NodeInputExpression field={filesField} command={command} />}
            {sessionIdField && <NodeInputExpression field={sessionIdField} command={command} />}
            {includeSessionToolsField && state?.session_id?.value && (
              <NodeInputExpression field={includeSessionToolsField} command={command} />
            )}
            {senderIdField && state?.session_id?.value && (
              <NodeInputExpression field={senderIdField} command={command} />
            )}
          </NodeSidebarTab>
          <Divider sx={{ marginBottom: '4rem' }} />
        </TabPanel>
        <TabPanel value='2'>
          <NodeSidebarTab>
            {modelField && <NodeInputExpression field={modelField} command={command} />}
            {routerMethodField && <NodeInputExpression field={routerMethodField} command={command} />}
            {rewriteQuestionField && <NodeInputExpression field={rewriteQuestionField} command={command} />}
            {systemField && <NodeInputExpression field={systemField} command={command} />}
            <AssistantRouterSystem
              assistants={state.assistants
                .map((assistantId) => assistants.find((assistant) => assistant.id === assistantId))
                .filter((assistant) => assistant !== undefined)}
              system={state.system.value}
            />
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='3'>
          <NodeData command={command} />
        </TabPanel>
        <TabPanel value='4'>
          <NodeSidebarTab>
            {isPaidModel(state.model) && (
              <Stack spacing={1}>
                <ModelAuthKey
                  command={command}
                  secrets={secrets}
                  secretsLoading={loading}
                  secretsError={error}
                  refetch={refetch}
                  setTab={setTab}
                />
                <SubText>This is the model used by the router to select the best assistant.</SubText>
              </Stack>
            )}
            {isPaidModel(state.model) && <Divider />}
            <Alert
              severity='warning'
              action={
                <Button customColor='orange' onClick={() => openAssistants()}>
                  Open
                </Button>
              }
            >
              The API key used for assistants is now defined in the assistant configuration settings. If you previously
              set the API key here, it is still saved and will continue to work. To update the API key, please update
              the assistant configuration.
            </Alert>
          </NodeSidebarTab>
        </TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
